<template>
  <div class="h-100 d-flex flex-column justify-space-between">
    <div
      v-if="isLoading"
      class="d-flex flex-column align-center justify-center text-center h-100"
    >
      <LoaderSpinner class="h-100" />
    </div>

    <div
      v-else-if="!isLoading"
      class="d-flex flex-column justify-space-between"
    >
      <div
        class="d-flex justify-space-between align-center px-6"
        style="height: 56px"
      >
        <p class="body-1 font-weight-medium pa-0 ma-0">
          {{ $t('routines.edit_finding') }}
        </p>
        <div>
          <FindingStatus
            large
            :status="selectedFinding.status"
            :canEdit="!hasRev"
            @onChange="handleSelectStatus"
          />
          <ChangedModalStatus
            :placeholder="$t('common.cancel_finding')"
            :show="showStatusDialog"
            :status="form.status"
            :subtitle="changedModalStatusText.subtitle"
            :title="changedModalStatusText.title"
            @updateModalStatus="handleChangeModalStatus"
            @hideModal="handleChangeModalStatus"
          />
        </div>
      </div>

      <v-divider />

      <div style="height: calc(100vh - 56px - 84px); overflow-y: scroll;">
        <div class="mb-4 px-6 mt-4">
          <v-alert
            v-if="selectedFinding.revId"
            border="left"
            class="text-caption"
            color="amber"
            colored-border
            dense
            icon="mdi-alert"
          >
            {{ alertText }}
          </v-alert>
        </div>

        <v-textarea
          v-model="form.situation"
          auto-grow
          dense
          outlined
          required
          rows="1"
          class="mt-6 px-6"
          :label="`${$t('routines.finding_one')}*`"
          :disabled="isLoading"
          :maxlength="situationMaxLength"
          :rules="[
            situation =>
              !!situation || $t('validation.form.this_field_is_required'),
          ]"
        />
        <AssigneesSelector
          multiple
          class="px-6"
          :class="{
            'mb-3':
              form.assignees.length > $constants.routines.assignees.maxLength,
          }"
          :disabled="isLoading"
          :maxSelectableAccounts="$constants.routines.assignees.maxLength"
          :selectedAccountIds="form.assignees"
          @change="form.assignees = $event"
        />

        <div class="mt-2 px-6">
          <p class="text-subtitle-2 font-weight-bold">
            {{ $t('routines.findings_classification') }}
          </p>

          <TagsSelector
            class="mb-6"
            data-private
            :allowedTags="routineAvailableTagsForFindings"
            :presetTags="routinePresetTags"
            :siteId="siteId"
            :value="tagsValue"
            @change="handleTagsChange"
          />

          <CategoriesSelector
            class="mb-6"
            data-private
            v-model="formCategories"
            :siteIds="siteIds"
          />

          <SituationTypesSelector
            v-if="form.revType === QUICK_ACTION"
            class="mb-6"
            v-model="form.situationTypes"
            :presetSituationTypes="routinePresetSituationTypes"
          />
        </div>

        <div class="mt-8 px-6">
          <p class="text-subtitle-2 font-weight-bold">
            {{ $t('routines.findings_schedule') }}
          </p>
          <PrioritySelector
            class="mb-6"
            :priority="findingPriority"
            @change="handlePriorityChange"
          />

          <DueDatePicker
            isEditMode
            :dueDateTime="form.dueDate"
            @change="handleSetDueDate"
          />
        </div>

        <div class="my-4 px-6">
          <p class="grey--text text--darken-1 font-size-12 ma-0 pa-0 mb-1">
            {{ $t('assignments.solution_evidence_label') }}
          </p>
          <MediaContainer
            ref="mediaContainer"
            onlyThumbnails
            allowMultipleSelect
            :images="form.media"
            @change="handleMediaChange"
          />
        </div>
        <ChangesDialog
          :showDialog="showDialog"
          @close="showDialog = false"
          @discard="handleDiscardChanges"
        />
      </div>
    </div>
    <div
      v-if="!isLoading"
      class="d-flex align-center px-4"
      style="height: 84px;"
    >
      <v-row class="ma-0 pa-0 d-flex align-center">
        <v-col>
          <Button
            block
            color="grey"
            outlined
            :text="$t('common.button.cancel')"
            @click="handleCancel"
          />
        </v-col>
        <v-col>
          <Button
            block
            color="primary"
            :loading="isLoading"
            :disabled="disableSaveButton"
            :text="$t('common.button.save')"
            @click="handleSave"
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import userMixin from '@/mixins/userMixin.vue'
import revMixin from '@/mixins/revMixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import CategoriesSelector from '@/components/CategoriesSelector.vue'
import TagsSelector from '@/components/TagsSelector/TagsSelector.vue'
import DueDatePicker from '@/components/DueDatePicker/DueDatePicker.vue'
import LoaderSpinner from '@/components/utils/LoaderSpinner.vue'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import AssigneesSelector from '@/components/AssigneesSelector/AssigneesSelector.vue'
import SituationTypesSelector from '@/components/SituationTypesSelector.vue'
import PrioritySelector from '@/components/PrioritySelector/PrioritySelector.vue'
import ChangesDialog from './ChangesDialog.vue'
import Button from '@/components/UI/Button'
import FindingStatus from '../FindingDrawer/FindingStatus.vue'
import ChangedModalStatus from '@/containers/Modals/ChangedModalStatus.vue'

export default {
  name: 'EditFinding',

  mixins: [mixin, userMixin, revMixin, trackerMixin],

  components: {
    LoaderSpinner,
    MediaContainer,
    AssigneesSelector,
    DueDatePicker,
    SituationTypesSelector,
    CategoriesSelector,
    TagsSelector,
    PrioritySelector,
    ChangesDialog,
    Button,
    FindingStatus,
    ChangedModalStatus,
  },

  props: {
    finding: Object,
    routine: Object,
  },

  data() {
    return {
      showDialog: false,
      showStatusDialog: false,
      isLoading: true,
      selectedFinding: {},
      form: {},
      routinePresetTags: [],
      routinePresetSituationTypes: [],
      routineAvailableTagsForFindings: [],
      QUICK_ACTION: this.pascalCase(this.$constants.revTypes.quickAction),
    }
  },

  async mounted() {
    await this.setData()
  },

  computed: {
    ...mapGetters(['organizationId', 'organization']),

    defaultRevType() {
      if (this.organization?.featuresEnabled__quickAction) {
        return this.pascalCase(this.$constants.revTypes.quickAction)
      }

      if (this.organization?.featuresEnabled__improvementIdea) {
        return this.pascalCase(this.$constants.revTypes.improvementIdea)
      }

      return null
    },

    hasRev() {
      return Boolean(this.finding?.revId)
    },

    changedModalStatusText() {
      if (this.form.status == this.$constants.findings.status.canceled)
        return {
          title: this.$t('common.nice_try'),
          subtitle: this.$t('common.marked_finding_as_canceled'),
        }
      return {
        title: this.$t('common.need_a_break'),
        subtitle: this.$t('common.marked_finding_as_onHold'),
      }
    },

    tagsValue() {
      return this.form?.tags ?? []
    },

    showActions() {
      return (
        !this.isLoading && !this.isCompleted && !this.selectedFinding?.revId
      )
    },

    disableSaveButton() {
      return (
        !this.form.situation ||
        this.form.assignees.length > 5 ||
        (this.isFindingTagsRequired && _.isEmpty(this.form.tags))
      )
    },

    isFindingTagsRequired() {
      return Boolean(this.routine?.requireFindingTags)
    },

    findingHasChanged() {
      return !_.isEqual(this.selectedFinding, this.form)
    },

    alertText() {
      const revType =
        this.selectedFinding.revType ??
        this.defaultRevType ??
        this.pascalCase(this.$constants.revTypes.quickAction)

      return this.$t('assignments.edit_finding_alert', {
        revType: this.$t(`common.rev_type.${_.snakeCase(revType)}_one`),
      })
    },

    formCategories: {
      get() {
        return this.form.categories ?? []
      },
      set(val) {
        this.form.categories = val
      },
    },

    findingPriority() {
      return this.selectedFinding?.priority ?? null
    },

    situationMaxLength() {
      return this.$constants.findings.situationMaxLength
    },

    siteId() {
      return this.selectedFinding.site?._id ?? this.selectedFinding.site
    },

    siteIds() {
      return [this.siteId]
    },
  },

  methods: {
    handleTagsChange(value) {
      this.form = { ...this.form, ...value }
    },

    async getTeam() {
      try {
        const response = await api.teams.getTeamById(
          this.organizationId,
          this.routine?.selectedTeam.id
        )

        return response.data
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async setData() {
      try {
        this.isLoading = true
        const response = await api.routine.getById(
          this.organizationId,
          this.routine.routineDefinition
        )
        this.routinePresetTags = response.data.presetTags
        this.routinePresetSituationTypes = response.data.presetSituationTypes
        this.routineAvailableTagsForFindings =
          response.data.availableTagsForFindings ?? []
        this.selectedFinding = this.formatFinding(this.finding)

        this.form = _.clone(this.selectedFinding)
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    handleMediaChange(media) {
      this.form.media = media
    },

    handlePriorityChange(priority) {
      this.form.priority = priority
    },

    formatFinding(finding) {
      return {
        ...finding,
        revType: finding.revType ?? this.defaultRevType,
        dueDate: finding?.dueDate,
        assignees: finding?.assignees?.map?.(assignee => assignee._id) ?? [],
      }
    },

    handleSelectStatus(status) {
      if (status == this.$constants.findings.status.completed)
        return this.showError(this.$t('routines.mark_finding_as_completed'))
      this.form.status = status
      if (
        status == this.$constants.findings.status.canceled ||
        status == this.$constants.findings.status.onHold
      ) {
        this.showStatusDialog = true
        return this.showStatusDialog
      }
      this.handleSave(false)
      this.selectedFinding.status = status
    },

    handleChangeModalStatus(payload = {}) {
      if (payload.status == this.$constants.findings.status.canceled)
        this.form.canceledReason = payload.reason
      if (payload.status == this.$constants.findings.status.onHold)
        this.form.onHoldReason = payload.reason

      this.handleSave(false)
      this.selectedFinding.status = this.form.status
      this.showStatusDialog = false
    },

    async handleSave(shouldEmit = true) {
      if (this.isFindingTagsRequired && _.isEmpty(this.form.tags)) {
        this.showError(this.$t('routines.finding_tags_required'))
        return
      }

      try {
        this.isLoading = true
        await api.findings.update({
          organizationId: this.organizationId,
          findingId: this.finding.id,
          data: {
            ...this.form,
            tags: this.form?.tags?.map?.(tag => tag?._id ?? tag) ?? [],
            media: this.form?.media?.map?.(media => media?.id ?? media),
            assignees: this.form?.assignees,
          },
        })

        this.trackEditFinding({
          fieldsChanged: this.getChangedFields(this.selectedFinding, this.form),
          situation: this.form?.situation ?? '',
          hasMedia: Boolean(this.form?.media?.length),
          hasAssignees: Boolean(this.form?.assignees?.length),
          numberOfAssignees: this.form?.assignees?.length ?? 0,
          revType: this.form?.revType,
          hasDueDate: Boolean(this.form?.dueDate),
        })

        if (shouldEmit) this.$emit('saved')
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    getChangedFields(object1, object2) {
      return _.reduce(
        object1,
        (result, value, key) => {
          return _.isEqual(value, object2[key]) ? result : result.concat(key)
        },
        []
      )
    },

    handleCancel() {
      if (this.findingHasChanged) {
        this.showDialog = true
        return this.showDialog
      }

      this.$emit('cancel')

      this.selectedFinding = {}
      this.form = {}
    },

    handleDiscardChanges() {
      this.showDialog = false
      this.selectedFinding = {}
      this.form = {}
      this.$emit('cancel')
    },

    handleSetDueDate(date) {
      this.form.dueDate = date
    },
  },
}
</script>
